import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useDashboardEvents } from "@/hooks/useEvents";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calendar, Video, Clock, X, CalendarDays, ExternalLink } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIVE_MEETING_STATUSES } from "@/lib/meetingStatus";
import { supabase } from "@/integrations/supabase/client";
import { format, isFuture, isPast } from "date-fns";
import { CancelMeetingDialog } from "@/components/schedule/CancelMeetingDialog";

interface ScheduledMeeting {
  id: string;
  status: string;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  zoom_join_url: string | null;
  zoom_start_url: string | null;
  mentor_id: string | null;
  entrepreneur_id: string | null;
  mentorship_request_id: string | null;
  duration_minutes: number | null;
  otherPerson: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

export default function Schedule() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: events, isLoading: eventsLoading } = useDashboardEvents();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  const queryClient = useQueryClient();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<{
    meetingId: string;
    otherPersonName: string;
    scheduledAt: string | null;
  } | null>(null);

  const { data: scheduledCalls, isLoading: callsLoading } = useQuery({
    queryKey: ["scheduled_mentorship_meetings", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch mentorship meetings where user is mentor or entrepreneur
      const { data: meetings, error: meetingsError } = await supabase
        .from("mentorship_meetings")
        .select(`
          id,
          status,
          scheduled_start_at,
          scheduled_end_at,
          zoom_join_url,
          zoom_start_url,
          mentor_id,
          entrepreneur_id,
          mentorship_request_id,
          duration_minutes
        `)
        .or(`mentor_id.eq.${user.id},entrepreneur_id.eq.${user.id}`)
        .in("status", ACTIVE_MEETING_STATUSES)
        .not("scheduled_start_at", "is", null)
        .order("scheduled_start_at", { ascending: true });

      if (meetingsError) throw meetingsError;
      if (!meetings || meetings.length === 0) return [];

      // Get profile info for the other participants
      const participantIds = meetings.flatMap(m => 
        [m.mentor_id, m.entrepreneur_id].filter((id): id is string => id !== null && id !== user.id)
      );
      
      const uniqueParticipantIds = [...new Set(participantIds)];
      
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", uniqueParticipantIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return meetings.map(meeting => ({
        ...meeting,
        otherPerson: profileMap.get(
          meeting.mentor_id === user.id ? meeting.entrepreneur_id! : meeting.mentor_id!
        ) || null
      })) as ScheduledMeeting[];
    },
    enabled: !!user?.id,
  });

  const handleCancelClick = (meeting: ScheduledMeeting) => {
    setSelectedMeeting({
      meetingId: meeting.id,
      otherPersonName: meeting.otherPerson?.full_name || "User",
      scheduledAt: meeting.scheduled_start_at,
    });
    setCancelDialogOpen(true);
  };

  const handleCancelled = () => {
    queryClient.invalidateQueries({ queryKey: ["scheduled_mentorship_meetings"] });
    queryClient.invalidateQueries({ queryKey: ["mentorship_meetings"] });
  };

  const isMentor = profile?.role === "mentor";
  const upcomingCalls = scheduledCalls?.filter((c) => 
    c.scheduled_start_at && isFuture(new Date(c.scheduled_start_at))
  ) || [];
  const pastCalls = scheduledCalls?.filter((c) => 
    c.scheduled_start_at && isPast(new Date(c.scheduled_start_at))
  ) || [];
  
  // Filter upcoming events (future events only)
  const upcomingEvents = events?.filter((e) => isFuture(new Date(e.event_date))).slice(0, 5) || [];

  return (
    <DashboardLayout>
      {(authLoading || profileLoading) ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (!user || !profile) ? null : (
      <>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Schedule</h1>
          <p className="text-muted-foreground">Manage your upcoming calls and events</p>
        </div>

        {/* Upcoming Calls */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Upcoming Calls
          </h2>

          {callsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : upcomingCalls.length > 0 ? (
            <div className="grid gap-4">
              {upcomingCalls.map((meeting) => (
                <Card key={meeting.id} className="border-accent/30">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={meeting.otherPerson?.avatar_url || undefined} />
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {meeting.otherPerson?.full_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{meeting.otherPerson?.full_name || "Unknown"}</p>
                          <p className="text-sm text-muted-foreground">
                            {meeting.scheduled_start_at && format(new Date(meeting.scheduled_start_at), "EEEE, MMMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {meeting.duration_minutes || 30} min
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleCancelClick(meeting)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        {meeting.zoom_join_url && (
                          <Button size="sm" asChild className="bg-accent hover:bg-accent/90">
                            <a href={meeting.zoom_join_url} target="_blank" rel="noopener noreferrer">
                              <Video className="h-4 w-4 mr-1" />
                              Join Call
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No upcoming calls scheduled</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Upcoming Events */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Upcoming Events
            </h2>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/events">View All</Link>
            </Button>
          </div>

          {eventsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid gap-3">
              {upcomingEvents.map((event) => {
                const eventDate = new Date(event.event_date);
                const featuredImage = event.attachments?.find((a) => a.is_featured);
                
                return (
                  <Card key={event.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {featuredImage && (
                          <div className="hidden sm:block w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                            <img 
                              src={featuredImage.url} 
                              alt={event.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{event.title}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <CalendarDays className="h-3.5 w-3.5" />
                            <span>{format(eventDate, "EEEE, MMMM d, yyyy")}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            <span>
                              {format(eventDate, "h:mm a")}
                              {event.timezone && ` (${event.timezone})`}
                            </span>
                          </div>
                        </div>
                        {event.registration_link && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={event.registration_link} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3.5 w-3.5 mr-1" />
                              Register
                            </a>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <CalendarDays className="h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">No upcoming events</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Past Calls */}
        {pastCalls.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-muted-foreground">Past Calls</h2>
            <div className="grid gap-4">
              {pastCalls.map((meeting) => (
                <Card key={meeting.id} className="opacity-75">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={meeting.otherPerson?.avatar_url || undefined} />
                        <AvatarFallback>{meeting.otherPerson?.full_name?.charAt(0) || "?"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium">{meeting.otherPerson?.full_name || "Unknown"}</p>
                        <p className="text-sm text-muted-foreground">
                          {meeting.scheduled_start_at && format(new Date(meeting.scheduled_start_at), "MMMM d, yyyy")}
                        </p>
                      </div>
                      <Badge variant="outline">Completed</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedMeeting && (
        <CancelMeetingDialog
          open={cancelDialogOpen}
          onOpenChange={setCancelDialogOpen}
          meetingId={selectedMeeting.meetingId}
          otherPersonName={selectedMeeting.otherPersonName}
          scheduledAt={selectedMeeting.scheduledAt}
          onCancelled={handleCancelled}
        />
      )}
      </>
      )}
    </DashboardLayout>
  );
}
